import { CleanRelativeLink, extractRelativePath } from "components/Shared/DomainSplit";
import { isBlankTarget, relForBlankTarget } from "lib/linkExternalProps";
import {
  ariaLabelWithNewWindowNotice,
  newWindowNoticeTitle,
} from "lib/newWindowA11y";

type EhsanFloatingButtonProps = {
  locale?: string;
  show?: boolean;
  linkField?: {
    value?: {
      href?: string;
      target?: string;
      text?: string;
    };
  };
  iconField?: {
    value?: {
      src?: string;
      alt?: string;
      width?: number;
      height?: number;
    };
  };
};

/** Matches chat FAB stack: bottom-5 + chat button (~3.375rem) + gap-2 (0.5rem). */
const EHSAN_BOTTOM_OFFSET = "calc(1.25rem + 3.375rem + 0.5rem)";
const DISPLAY_SIZE_PX = 54;

const resolveIconSrc = (src: string): string => {
  const resolved = extractRelativePath(src);
  if (resolved.startsWith("http")) return resolved;
  const host = (process.env.NEXT_PUBLIC_SITECORE_API_HOST || "").replace(/\/$/, "");
  return `${host}${resolved.startsWith("/") ? resolved : `/${resolved}`}`;
};

const EhsanFloatingButton = ({
  locale,
  show,
  linkField,
  iconField,
}: EhsanFloatingButtonProps): JSX.Element | null => {
  if (!show) return null;

  const href = CleanRelativeLink(linkField?.value?.href);
  const iconSrc = iconField?.value?.src;
  if (!href || !iconSrc) return null;

  const target = (linkField?.value?.target || "_self").trim() || "_self";
  const linkText = linkField?.value?.text && String(linkField.value.text).trim();
  const isPlaceholderLabel = !linkText || linkText === "$name";
  const labelBase =
    (!isPlaceholderLabel && linkText) ||
    (iconField?.value?.alt && String(iconField.value.alt).trim()) ||
    "Ehsan";
  const ariaLabel = isBlankTarget(target)
    ? ariaLabelWithNewWindowNotice(labelBase, locale)
    : labelBase;

  return (
    <a
      href={href}
      target={target}
      rel={relForBlankTarget(target)}
      title={isBlankTarget(target) ? newWindowNoticeTitle(locale) : undefined}
      aria-label={ariaLabel}
      className="fixed right-5 z-[500] block shrink-0 cursor-pointer overflow-hidden rounded-full transition-opacity hover:opacity-90"
      style={{
        bottom: EHSAN_BOTTOM_OFFSET,
        width: DISPLAY_SIZE_PX,
        height: DISPLAY_SIZE_PX,
      }}
    >
      <img
        src={resolveIconSrc(iconSrc)}
        alt=""
        aria-hidden
        className="absolute inset-0 h-full w-full object-cover"
      />
    </a>
  );
};

export default EhsanFloatingButton;
